import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { anyService } from "@/services/breeze/AnyService";
import { entityService } from "@/services/breeze/EntityService";
import { IAny, PaginatedResult } from "@/types/breeze/Any";
import { EntityFieldTree, IEntity } from "@/types/breeze/Entity";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useAuth } from "@/providers/AuthProvider";

interface UseEntityDataOptions {
  initialPage?: number;
  initialPageSize?: number;
  disableFieldSelector?: boolean;
}

interface UseEntityDataReturn {
  // Data
  entityDefinition: IEntity | null;
  entityTree: EntityFieldTree[] | null;
  entityData: PaginatedResult<IAny> | null;
  isLoading: boolean;
  error: Error | null;

  // State
  selectedFields: string[];
  selectedItems: number[];
  page: number;
  pageSize: number;
  filterQuery: string;
  sortBy: string;
  sortDirection: string;
  viewType: string;

  // State setters
  setSelectedFields: (fields: string[]) => void;
  setSelectedItems: (items: number[]) => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setFilterQuery: (query: string) => void;
  setSort: (sortBy: string, sortDirection: string) => void;
  setViewType: (type: string) => void;
  invalidateQueryKey: () => void;

  // Actions
  exportData: (format: "csv" | "excel" | "pdf") => void;
  importData: (data: Partial<IAny>[]) => Promise<void>;
  deleteItems: (ids: number[]) => Promise<void>;
}

const STORAGE_KEY = "entity-field-selections";

interface FieldSelections {
  [key: string]: {
    // tenantId
    [key: string]: string[]; // entityName: selectedFields
  };
}

const getStoredSelections = (): FieldSelections => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error("Error loading field selections:", error);
    return {};
  }
};

const getFieldsForEntityAndTenant = (
  entityName: string,
  tenantId: number | null,
): string[] => {
  try {
    const selections = getStoredSelections();
    const tenantKey = String(tenantId || "default");
    return selections[tenantKey]?.[entityName] || [];
  } catch (error) {
    console.error("Error getting fields for entity:", error);
    return [];
  }
};

const saveFieldsForEntityAndTenant = (
  entityName: string,
  tenantId: number | null,
  fields: string[],
) => {
  try {
    const selections = getStoredSelections();
    const tenantKey = String(tenantId || "default");

    selections[tenantKey] = selections[tenantKey] || {};
    selections[tenantKey][entityName] = fields;

    localStorage.setItem(STORAGE_KEY, JSON.stringify(selections));
  } catch (error) {
    console.error("Error saving field selections:", error);
  }
};

export function useEntityData(
  entityName: string,
  options: UseEntityDataOptions = {},
): UseEntityDataReturn {
  const queryClient = useQueryClient();
  const { currentTenant } = useAuth();
  const [viewType, setViewType] = useState<string>("grid");

  const [selectedFields, setSelectedFields] = useState<string[]>(() =>
    getFieldsForEntityAndTenant(entityName, currentTenant?.id || null),
  );
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [page, setPage] = useState(options.initialPage || 1);
  const [pageSize, setPageSize] = useState(options.initialPageSize || 25);
  const [filterQuery, setFilterQuery] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("");

  const handleSetSort = (newSortBy: string, newSortDirection: string) => {
    setSortBy(newSortBy);
    setSortDirection(newSortDirection);
  };

  const handleSetSelectedFields = (fields: string[]) => {
    setSelectedFields(fields);
    saveFieldsForEntityAndTenant(entityName, currentTenant?.id || null, fields);
  };

  // Update selected fields when tenant changes
  useEffect(() => {
    const fields = getFieldsForEntityAndTenant(
      entityName,
      currentTenant?.id || null,
    );
    setSelectedFields(fields);
  }, [currentTenant?.id, entityName]);

  // Fetch entity definition
  const {
    data: entityDefinition,
    isLoading: isLoadingDefinition,
    error: definitionError,
  } = useQuery({
    queryKey: ["entity", entityName, true],
    queryFn: () => entityService.getByName(entityName, true),
  });

  // Fetch entity tree for field selection
  const {
    data: entityTree,
    isLoading: isLoadingTree,
    error: treeError,
  } = useQuery({
    queryKey: ["entity-tree", entityName],
    queryFn: () => anyService.hierarchy(entityName),
  });

  // Fetch entity data
  const {
    data: entityData,
    isLoading: isLoadingData,
    error: dataError,
  } = useQuery({
    queryKey: [
      "entities-data",
      entityName,
      selectedFields,
      filterQuery,
      sortBy,
      sortDirection,
      page,
      pageSize,
      viewType,
    ],
    queryFn: () =>
      viewType === "kanban"
        ? anyService.getAll(
            entityName,
            [],
            filterQuery,
            1,
            1000,
            sortBy,
            sortDirection,
          ) // Fetch more items for Kanban
        : anyService.getAll(
            entityName,
            options.disableFieldSelector ? [] : selectedFields,
            filterQuery,
            page,
            pageSize,
            sortBy,
            sortDirection,
          ),
    enabled: !!entityDefinition,
    staleTime: 0, // Data is immediately stale
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  const invalidateQueryKey = () => {
    queryClient.invalidateQueries({
      queryKey: [
        "entities-data",
        entityName,
        selectedFields,
        filterQuery,
        sortBy,
        sortDirection,
        page,
        pageSize,
        viewType,
      ],
    });
  };

  // Import mutation
  const importMutation = useMutation({
    mutationFn: async (data: Partial<IAny>[]) => {
      await anyService.import(entityName, data as IAny[]);
    },
    onSuccess: () => {
      invalidateQueryKey();
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      await anyService.deleteManyAny(entityName, ids);
    },
    onSuccess: () => {
      invalidateQueryKey();
      setSelectedItems([]);
    },
  });

  // Export functionality
  const exportData = (format: "csv" | "excel" | "pdf") => {
    if (!entityData?.items?.length) return;

    const fields =
      selectedFields.length > 0
        ? selectedFields
        : entityDefinition?.fields.map((f) => f.name) || [];

    // Helper function to get nested value
    const getNestedValue = (obj: any, path: string) => {
      return path.split(".").reduce((current, key) => current?.[key], obj);
    };

    // Transform data to flatten nested structures
    const flattenedData = entityData.items.map((item) => {
      return fields.reduce((acc: any, field: string) => {
        const value = getNestedValue(item, field);
        acc[field] =
          value === undefined || value === null
            ? ""
            : typeof value === "object"
              ? JSON.stringify(value)
              : String(value);
        return acc;
      }, {});
    });

    switch (format) {
      case "csv": {
        const csv = Papa.unparse(flattenedData, { header: true });
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${entityName}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        break;
      }
      case "excel": {
        const worksheet = XLSX.utils.json_to_sheet(flattenedData, {
          header: fields,
        });
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
        XLSX.writeFile(workbook, `${entityName}.xlsx`);
        break;
      }
      case "pdf": {
        const doc = new jsPDF();
        doc.text(`${entityName} Data`, 14, 10);
        autoTable(doc, {
          head: [
            fields.map((field) => {
              // Format header names consistently with the grid
              const parts = field.split(".");
              if (parts.length > 1) {
                const parentPart =
                  parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
                const childPart =
                  parts[parts.length - 1].charAt(0).toUpperCase() +
                  parts[parts.length - 1].slice(1);
                return `${parentPart} ${childPart}`;
              }
              return field.charAt(0).toUpperCase() + field.slice(1);
            }),
          ],
          body: flattenedData.map((item) => fields.map((field) => item[field])),
        });
        doc.save(`${entityName}.pdf`);
        break;
      }
    }
  };

  return {
    // Data
    entityDefinition: entityDefinition || null,
    entityTree: entityTree || null,
    entityData: entityData || null,
    isLoading: isLoadingDefinition || isLoadingTree || isLoadingData,
    error: definitionError || treeError || dataError,

    // State
    selectedFields,
    selectedItems,
    page,
    pageSize,
    filterQuery,
    sortBy,
    sortDirection,
    viewType,

    // State setters
    setSelectedFields: handleSetSelectedFields,
    setSelectedItems,
    setPage,
    setPageSize,
    setFilterQuery,
    setSort: handleSetSort,
    setViewType,
    invalidateQueryKey,

    // Actions
    exportData,
    importData: (data) => importMutation.mutateAsync(data),
    deleteItems: (ids) => deleteMutation.mutateAsync(ids),
  };
}
